import {useQuery} from "@apollo/client";

import {GQL_PRIVATE_STATUSPAGE_QUERY, GQL_PUBLIC_STATUSPAGE_QUERY} from "@/lib/queries/statusPageConsumption.ts";
import {ComponentStatus} from "@/lib/types/statuspages.ts";

import type {PublicStatusPage} from "../types.ts";

interface UseStatusPageDataResult {
  statusPage: PublicStatusPage | null;
  componentStatuses: ComponentStatus[];
  loading: boolean;
  error: Error | null;
}

/**
 * Maps the GraphQL component_status_metadata array to the ComponentStatus shape
 * expected by the UI components.
 */
const mapComponentStatuses = (metadata: PublicStatusPage["component_status_metadata"]): ComponentStatus[] => {
  return (metadata ?? []).map((m) => ({
    value: m.value as ComponentStatus["value"],
    rank: m.rank,
    display: m.display,
    color: m.color,
    is_operational: m.is_operational,
    is_failure: !m.is_operational,
  }));
};

export const useStatusPageData = (slug: string, mode: "public" | "private" = "public"): UseStatusPageDataResult => {
  const isPrivate = mode === "private";
  const parsedId = isPrivate ? parseInt(slug, 10) : undefined;

  const {
    data: publicData,
    loading: publicLoading,
    error: publicError,
  } = useQuery<{publicStatusPage: PublicStatusPage | null}>(GQL_PUBLIC_STATUSPAGE_QUERY, {
    variables: {slug},
    skip: isPrivate || !slug,
  });

  const {
    data: privateData,
    loading: privateLoading,
    error: privateError,
  } = useQuery<{privateStatusPage: PublicStatusPage | null}>(GQL_PRIVATE_STATUSPAGE_QUERY, {
    variables: {id: parsedId ?? 0},
    skip: !isPrivate || !parsedId,
  });

  const loading = isPrivate ? privateLoading : publicLoading;
  const rawError = isPrivate ? privateError : publicError;
  const rawPage = isPrivate ? privateData?.privateStatusPage : publicData?.publicStatusPage;

  const statusPage = rawPage ?? null;
  const componentStatuses = rawPage ? mapComponentStatuses(rawPage.component_status_metadata) : [];

  return {
    statusPage,
    componentStatuses,
    loading,
    error: rawError ? new Error(rawError.message) : null,
  };
};
